import { produce } from 'immer'; // 불변성 관리를 위한 immer 추가
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { TrimRange, TrimState } from '@/shared/types/trim';
import { DtoAspectRatio } from '@/shared/constants';
import {
  DEFAULT_CAPTION_TARGET_LANGUAGE,
  type CaptionTargetLanguage,
} from '@/shared/lib/captions/caption-translation-languages';

/**
 * LTS 모델 선택 옵션 타입
 */
export type LtsModelOptionId =
  | 'light'
  | 'advanced'
  | 'multimodal'
  | 'portrait'
  /** 미드폼 분석 (indexLevel 50, 정식 오픈) */
  | 'midform';
export type AnalysisFilterMode = 'off' | 'penalty' | 'exclude';
export type StaticScreenFilterMode = AnalysisFilterMode;

export interface TargetLengthRangeSec {
  minSec: number;
  maxSec: number;
}

export const DEFAULT_TARGET_LENGTH_RANGE_SEC: TargetLengthRangeSec = {
  minSec: 30,
  maxSec: 60,
};

/**
 * 폴더 스택 항목 타입 정의
 */
export interface FolderStackType {
  id: number;
  name: string;
  isRoot?: boolean;
}

/**
 * 홈 업로드 타입 정의
 */
export type LongToShortUploadType = 'local' | 'url' | 'space' | null;

/**
 * 파일 업로드 상태 표시 타입
 */
export type FileUploadStatusViewType = 'textField' | 'indicator';

/**
 * 홈 상태 타입 정의
 */
export interface LongToShortStateType {
  searchUrl: string;
  uploadType: LongToShortUploadType;
  selectedContentId: number | null;
  folderStack: FolderStackType[];
  folderId: number | null;
  isVideoUploadDropdownOpen: boolean;
  presignUploadFileId: number | null;
  projectId: number | null;
  isShowHomeVideoList: boolean;
  isOpenMySpaceDialog: boolean;
  isOpenAdditionalProjectInfoDialog: boolean;
  fileUploadStatusViewType: FileUploadStatusViewType;
  isLoading: boolean;
  isUploading: boolean; // 업로드 진행 중인지 여부 (0 < progress < 100)
  triggerFileUpload: boolean; // 파일 업로드 트리거
  brandTemplateId: number | string | null;
  /** 브랜드 템플릿 기반 aspect ratio */
  aspectRatio: DtoAspectRatio | null;
  /** 트림 구간 정보 (null이면 전체 영상 업로드) */
  trimRange: TrimRange | null;
  /** 트림 상태 */
  trimState: TrimState;
  /** 트림 진행률 (0-100) */
  trimProgress: number;
  /** 프록시 생성 상태 (데스크톱 전용) */
  proxyState: TrimState;
  /** 프록시 생성 진행률 (0-100) */
  proxyProgress: number;
  /** 선택된 분석 모드 */
  selectedModelId: LtsModelOptionId | null;
  /** 자동 목표 길이: true(기본)면 영상 길이 분포로 ai-api 가 target 자동 도출, false면 수동 슬라이더 범위 사용 */
  autoTargetLength: boolean;
  targetLengthRangeSec: TargetLengthRangeSec;
  /** GS 분석 필터: 정확한 가격 언급 구간 제외 (기본 true=제외, 현행 동작) */
  excludeExactPrice: boolean;
  exactPriceFilterMode: AnalysisFilterMode;
  /** GS 분석 필터: 시청자 채팅 반응 구간 제외 (기본 true=제외, 현행 동작) */
  excludeCommentReaction: boolean;
  commentReactionFilterMode: AnalysisFilterMode;
  /** GS 분석 필터: L바·전면 상품 화면·스틸컷 같은 정적 화면 필터링 */
  excludeStaticScreens: boolean;
  /** GS 분석 필터: 정적 화면 필터링 강도 */
  staticScreenFilterMode: StaticScreenFilterMode;
  /** GS 분석 필터: 사용자가 직접 입력한 제외 키워드 목록 */
  excludeKeywords: string[];
  isCommerceAnalysisEnabled: boolean;
  /** 자막 번역: 사용 여부 (정책 노출 space 에서만 화면에 나온다) */
  captionTranslationEnabled: boolean;
  /** 자막 번역: 타겟 언어 (자막 번역 ON 일 때만 요청에 실린다) */
  captionTargetLanguage: CaptionTargetLanguage;
  /** GS 분석 필터: 쇼핑호스트 우선 감지 사용 여부 */
  isVoiceTargetDetectionEnabled: boolean;
  /** GS 분석 필터: 우선 감지 쇼핑호스트 code 목록 (null=전체 선택, []=우선 감지 OFF payload) */
  selectedVoiceCodes: number[] | null;
  skipGsrSplitting: boolean;
}

/**
 * 홈 파일 메타데이터 타입 정의
 */
export interface LongToShortFileType {
  file?: File | null;
  uploadProgress?: number;
  videoUrl?: string;
  imageUrl?: string;
  duration?: number;
  resolution?: string;
  fileSize?: number;
  fileName?: string;
  fileExtension?: string;
  width?: number;
  height?: number;
}

/**
 * 전체 홈 스토어 상태
 */
interface LongToShortState {
  state: LongToShortStateType;
  metadata: LongToShortFileType;
}

/**
 * 홈 상태 액션 인터페이스
 */
interface LongToShortStateActions {
  setSearchUrl: (url: string) => void;
  setUploadType: (type: LongToShortUploadType) => void;
  setSelectedContentId: (id: number | null) => void;
  setFolderStack: (
    folderStackOrUpdater: (prev: FolderStackType[]) => FolderStackType[],
  ) => void;
  setFolderId: (id: number | null) => void;
  setIsVideoUploadDropdownOpen: (open: boolean) => void;
  setPresignUploadFileId: (id: number) => void;
  setProjectId: (id: number) => void;
  setIsShowHomeVideoList: (show: boolean) => void;
  setMetadata: (metadata: LongToShortFileType) => void;
  setIsOpenMySpaceDialog: (open: boolean) => void;
  setIsOpenAdditionalProjectInfoDialog: (open: boolean) => void;
  setFileUploadStatusViewType: (type: FileUploadStatusViewType) => void;
  setIsLoading: (isLoading: boolean) => void;
  setTriggerFileUpload: (trigger: boolean) => void;
  setBrandTemplateId: (id: number | string | null) => void;
  setAspectRatio: (aspectRatio: DtoAspectRatio | null) => void;
  setTrimRange: (range: TrimRange | null) => void;
  setTrimState: (state: TrimState) => void;
  setTrimProgress: (progress: number) => void;
  setProxyState: (state: TrimState) => void;
  setProxyProgress: (progress: number) => void;
  setSelectedModelId: (id: LtsModelOptionId | null) => void;
  setAutoTargetLength: (auto: boolean) => void;
  setTargetLengthRangeSec: (range: TargetLengthRangeSec) => void;
  setIsCommerceAnalysisEnabled: (enabled: boolean) => void;
  setCaptionTranslationEnabled: (enabled: boolean) => void;
  setCaptionTargetLanguage: (language: CaptionTargetLanguage) => void;
  setExcludeExactPrice: (exclude: boolean) => void;
  setExactPriceFilterMode: (mode: AnalysisFilterMode) => void;
  setExcludeCommentReaction: (exclude: boolean) => void;
  setCommentReactionFilterMode: (mode: AnalysisFilterMode) => void;
  setExcludeStaticScreens: (exclude: boolean) => void;
  setStaticScreenFilterMode: (mode: StaticScreenFilterMode) => void;
  setExcludeKeywords: (keywords: string[]) => void;
  setIsVoiceTargetDetectionEnabled: (enabled: boolean) => void;
  setSelectedVoiceCodes: (codes: number[] | null) => void;
  setSkipGsrSplitting: (skip: boolean) => void;
}

/**
 * 홈 파일 액션 인터페이스
 */
interface LongToShortFileActions {
  setFile: (file: File | null) => void;
  setUploadProgress: (progress: number) => void;
  setVideoUrl: (url: string) => void;
  setImageUrl: (url: string) => void;
  setDuration: (duration: number) => void;
  setResolution: (resolution: string) => void;
  setFileName: (name: string) => void;
  setFileExtension: (extension: string) => void;
  setFileSize: (fileSize: number) => void;
  updateMetadata: (data: Partial<LongToShortFileType>) => void;
  reset: () => void;
  setWidth: (width: number) => void;
  setHeight: (height: number) => void;
}

/**
 * 홈 스토어 타입 - 상태와 액션을 모두 포함
 */
export type LongToShortStore = LongToShortState &
  LongToShortFileActions &
  LongToShortStateActions;

/**
 * 초기 상태 정의
 */
const initialState: LongToShortState = {
  state: {
    searchUrl: '',
    uploadType: null,
    selectedContentId: null,
    folderStack: [],
    folderId: null,
    isVideoUploadDropdownOpen: false,
    presignUploadFileId: null,
    projectId: null,
    isShowHomeVideoList: false,
    isOpenMySpaceDialog: false,
    isOpenAdditionalProjectInfoDialog: false,
    fileUploadStatusViewType: 'textField',
    isLoading: false,
    isUploading: false,
    triggerFileUpload: false,
    brandTemplateId: null,
    aspectRatio: null,
    trimRange: null,
    trimState: 'idle',
    trimProgress: 0,
    proxyState: 'idle',
    proxyProgress: 0,
    selectedModelId: null,
    autoTargetLength: true,
    targetLengthRangeSec: DEFAULT_TARGET_LENGTH_RANGE_SEC,
    isCommerceAnalysisEnabled: true,
    captionTranslationEnabled: false,
    captionTargetLanguage: DEFAULT_CAPTION_TARGET_LANGUAGE,
    excludeExactPrice: true,
    exactPriceFilterMode: 'penalty',
    excludeCommentReaction: true,
    commentReactionFilterMode: 'penalty',
    excludeStaticScreens: true,
    staticScreenFilterMode: 'penalty',
    excludeKeywords: [],
    // GSR/GSMYSHOP "우선 감지 쇼핑호스트" UI 제거에 따라 기본 OFF.
    // false → buildAnalysisOptions 가 voiceTargets:[] (우선 감지 OFF) 전송.
    isVoiceTargetDetectionEnabled: false,
    selectedVoiceCodes: null,
    skipGsrSplitting: false,
  },
  metadata: {
    file: null,
    uploadProgress: 0,
    videoUrl: '',
    imageUrl: '',
    duration: 0,
    resolution: '',
    fileName: '',
    fileExtension: '',
    fileSize: 0,
    width: 0,
    height: 0,
  },
};

/**
 * 홈 스토어 생성
 * 기능별로 슬라이스를 분리하여 구성
 */
export const useLongToShortStore = create<LongToShortStore>()(
  devtools(
    subscribeWithSelector((set) => {
      /**
       * 상태 업데이트 헬퍼 함수
       * 반복되는 setter 패턴을 줄이기 위한 유틸리티 함수
       */
      const createMetadataSetter =
        <K extends keyof LongToShortFileType>(key: K) =>
        (value: LongToShortFileType[K]) =>
          set(
            produce((state: LongToShortState) => {
              state.metadata[key] = value;
            }),
            false,
            `set${key.charAt(0).toUpperCase() + key.slice(1)}`,
          );

      const createStateSetter =
        <K extends keyof LongToShortStateType>(key: K) =>
        (value: LongToShortStateType[K]) =>
          set(
            produce((state: LongToShortState) => {
              state.state[key] = value;
            }),
            false,
            `set${key.charAt(0).toUpperCase() + key.slice(1)}`,
          );

      // 상태 슬라이스와 액션들 반환
      return {
        ...initialState,

        // 메타데이터 액션
        setFile: createMetadataSetter('file'),
        setUploadProgress: (progress: number) =>
          set(
            produce((state: LongToShortState) => {
              state.metadata.uploadProgress = progress;
              state.state.isLoading = progress !== 100;
              // isUploading: 업로드가 진행 중인지 여부 (0 < progress < 100)
              state.state.isUploading = progress > 0 && progress < 100;
            }),
            false,
            'setUploadProgress',
          ),
        setVideoUrl: createMetadataSetter('videoUrl'),
        setImageUrl: createMetadataSetter('imageUrl'),
        setDuration: createMetadataSetter('duration'),
        setResolution: createMetadataSetter('resolution'),
        setFileName: createMetadataSetter('fileName'),
        setFileExtension: createMetadataSetter('fileExtension'),
        setFileSize: createMetadataSetter('fileSize'),
        setWidth: createMetadataSetter('width'),
        setHeight: createMetadataSetter('height'),

        updateMetadata: (data: Partial<LongToShortFileType>) =>
          set(
            produce((state: LongToShortState) => {
              Object.assign(state.metadata, data);
            }),
            false,
            'updateMetadata',
          ),

        // 상태 액션들
        setSearchUrl: createStateSetter('searchUrl'),
        setUploadType: createStateSetter('uploadType'),
        setSelectedContentId: createStateSetter('selectedContentId'),
        setIsVideoUploadDropdownOpen: createStateSetter(
          'isVideoUploadDropdownOpen',
        ),
        setPresignUploadFileId: createStateSetter('presignUploadFileId'),
        setProjectId: createStateSetter('projectId'),
        setIsShowHomeVideoList: createStateSetter('isShowHomeVideoList'),
        setIsOpenMySpaceDialog: createStateSetter('isOpenMySpaceDialog'),
        setIsOpenAdditionalProjectInfoDialog: createStateSetter(
          'isOpenAdditionalProjectInfoDialog',
        ),
        setFileUploadStatusViewType: createStateSetter(
          'fileUploadStatusViewType',
        ),
        setIsLoading: createStateSetter('isLoading'),
        setTriggerFileUpload: createStateSetter('triggerFileUpload'),
        setBrandTemplateId: createStateSetter('brandTemplateId'),
        setAspectRatio: createStateSetter('aspectRatio'),
        setTrimRange: createStateSetter('trimRange'),
        setTrimState: createStateSetter('trimState'),
        setTrimProgress: createStateSetter('trimProgress'),
        setProxyState: createStateSetter('proxyState'),
        setProxyProgress: createStateSetter('proxyProgress'),
        setSelectedModelId: createStateSetter('selectedModelId'),
        setAutoTargetLength: createStateSetter('autoTargetLength'),
        setTargetLengthRangeSec: createStateSetter('targetLengthRangeSec'),
        setIsCommerceAnalysisEnabled: createStateSetter(
          'isCommerceAnalysisEnabled',
        ),
        setCaptionTranslationEnabled: createStateSetter(
          'captionTranslationEnabled',
        ),
        setCaptionTargetLanguage: createStateSetter('captionTargetLanguage'),
        setExcludeExactPrice: (exclude: boolean) =>
          set(
            produce((state: LongToShortState) => {
              state.state.excludeExactPrice = exclude;
              state.state.exactPriceFilterMode = exclude ? 'penalty' : 'off';
            }),
          ),
        setExactPriceFilterMode: (mode: AnalysisFilterMode) =>
          set(
            produce((state: LongToShortState) => {
              state.state.exactPriceFilterMode = mode;
              state.state.excludeExactPrice = mode !== 'off';
            }),
          ),
        setExcludeCommentReaction: (exclude: boolean) =>
          set(
            produce((state: LongToShortState) => {
              state.state.excludeCommentReaction = exclude;
              state.state.commentReactionFilterMode = exclude
                ? 'penalty'
                : 'off';
            }),
          ),
        setCommentReactionFilterMode: (mode: AnalysisFilterMode) =>
          set(
            produce((state: LongToShortState) => {
              state.state.commentReactionFilterMode = mode;
              state.state.excludeCommentReaction = mode !== 'off';
            }),
          ),
        setExcludeStaticScreens: (exclude: boolean) =>
          set(
            produce((state: LongToShortState) => {
              state.state.excludeStaticScreens = exclude;
              state.state.staticScreenFilterMode = exclude ? 'penalty' : 'off';
            }),
          ),
        setStaticScreenFilterMode: (mode: StaticScreenFilterMode) =>
          set(
            produce((state: LongToShortState) => {
              state.state.staticScreenFilterMode = mode;
              state.state.excludeStaticScreens = mode !== 'off';
            }),
          ),
        setExcludeKeywords: createStateSetter('excludeKeywords'),
        setIsVoiceTargetDetectionEnabled: createStateSetter(
          'isVoiceTargetDetectionEnabled',
        ),
        setSelectedVoiceCodes: createStateSetter('selectedVoiceCodes'),
        setSkipGsrSplitting: createStateSetter('skipGsrSplitting'),

        setFolderStack: (
          folderStackOrUpdater: (prev: FolderStackType[]) => FolderStackType[],
        ) =>
          set(
            produce((state: LongToShortState) => {
              state.state.folderStack = folderStackOrUpdater(
                state.state.folderStack,
              );
            }),
            false,
            'setFolderStack',
          ),

        setFolderId: (id: number | null) =>
          set(
            produce((state: LongToShortState) => {
              state.state.folderId = id;
            }),
            false,
            'setFolderId',
          ),

        setMetadata: (metadata: LongToShortFileType) =>
          set(
            produce((state: LongToShortState) => {
              Object.assign(state.metadata, metadata);
            }),
            false,
            'setMetadata',
          ),

        reset: () =>
          set(
            produce((state: LongToShortState) => {
              // 기존 videoUrl이 있다면 메모리 누수 방지를 위해 정리
              if (
                state.metadata.videoUrl &&
                state.metadata.videoUrl.startsWith('blob:')
              ) {
                try {
                  URL.revokeObjectURL(state.metadata.videoUrl);
                } catch (error) {
                  console.warn(
                    'Failed to revoke object URL during reset:',
                    error,
                  );
                }
              }

              // isRoot가 true인 폴더만 유지
              const rootFolders = state.state.folderStack.filter(
                (folder) => folder.isRoot === true,
              );

              // 초기 상태로 리셋하되 루트 폴더는 유지
              return {
                ...initialState,
                state: {
                  ...initialState.state,
                  folderStack: rootFolders,
                },
              };
            }),
            false,
            'reset',
          ),
      };
    }),
    {
      name: 'long-to-short-store',
    },
  ),
);
